import {
  createFormsTemplate,
  IFormsTemplate,
  updateFormsTemplate,
} from '@commenda/api/form-builder/forms-templates';
import mixpanel, {
  MIXPANEL_EVENTS,
  MIXPANEL_SURFACE_AREA,
} from '@commenda/mixpanel';
import type {
  FormBuilderState,
  FormComponent,
  FormDSL,
  FormModule,
  FormStep,
  Selection,
  TemplateMetadata,
} from '@commenda/types';
import { FormsTemplateStatus, FormsTemplateType } from '@commenda/types';
import {
  FORM_BUILDER_AUTO_SAVE_INTERVAL,
  FORM_BUILDER_STORAGE_KEY,
} from '@commenda/utils/form-builder';
import { useCallback, useEffect, useState } from 'react';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { createEmptyDSL } from '../constants';
import { generateId, isModuleLockingComponent } from '../types';

export interface UseFormBuilderOptions {
  initialDSL?: FormDSL;
  initialTemplate?: IFormsTemplate;
  skipLocalStorage?: boolean;
  // Namespace the draft so two hosts cannot restore each other's work.
  storageKey?: string;
}

export function useFormBuilder({
  initialDSL,
  initialTemplate,
  skipLocalStorage = false,
  storageKey = FORM_BUILDER_STORAGE_KEY,
}: UseFormBuilderOptions) {
  const { getItem, setItem } = useLocalStorage();

  const [state, setState] = useState<FormBuilderState>(() => {
    // If we have an initial template from API, use it
    if (initialTemplate) {
      return {
        dsl:
          (initialDSL || (initialTemplate?.dsl as unknown as FormDSL)) ??
          createEmptyDSL(),
        selection: { type: null },
        isDirty: false,
        lastSaved: new Date(initialTemplate.updatedAt),
        templateMetadata: {
          id: initialTemplate.id,
          name: initialTemplate.name,
          description: initialTemplate.description || undefined,
          status: initialTemplate.status as 'DRAFT' | 'LIVE' | 'COMPLETED',
          version: initialTemplate.version,
          versionNote: initialTemplate.versionNote || undefined,
          key: initialTemplate.key || undefined,
          keyName: initialTemplate.keyName || undefined,
        },
      };
    }

    // Try to load from localStorage if not skipping
    if (!skipLocalStorage) {
      const saved = getItem(storageKey);
      if (saved) {
        try {
          const parsedDSL = JSON.parse(saved) as FormDSL;
          return {
            dsl: parsedDSL,
            selection: { type: null },
            isDirty: false,
            lastSaved: new Date(),
          };
        } catch {
          // Invalid saved state, fall back to initial or empty
        }
      }
    }

    return {
      dsl: initialDSL || createEmptyDSL(),
      selection: { type: null },
      isDirty: false,
    };
  });

  const [isSaving, setIsSaving] = useState(false);

  // Auto-save to localStorage (only if not using API template)
  useEffect(() => {
    if (skipLocalStorage || !state.isDirty) return;

    const timeoutId = setTimeout(() => {
      setItem(storageKey, JSON.stringify(state.dsl));
      // Only reset isDirty if template already exists in API (has an ID).
      // For new templates, localStorage is just a backup - keep isDirty true
      // so the save button remains enabled until the user saves to API.
      if (state.templateMetadata?.id) {
        setState((prev) => ({
          ...prev,
          isDirty: false,
          lastSaved: new Date(),
        }));
      }
    }, FORM_BUILDER_AUTO_SAVE_INTERVAL);

    return () => clearTimeout(timeoutId);
  }, [
    skipLocalStorage,
    state.isDirty,
    state.dsl,
    state.templateMetadata?.id,
    setItem,
  ]);

  // Save immediately to localStorage
  const saveNow = useCallback(() => {
    if (skipLocalStorage) return;
    setItem(storageKey, JSON.stringify(state.dsl));
    setState((prev) => ({
      ...prev,
      isDirty: false,
      lastSaved: new Date(),
    }));
  }, [skipLocalStorage, setItem, state.dsl]);

  // Update template metadata
  const updateTemplateMetadata = useCallback(
    (updates: Partial<TemplateMetadata>) => {
      setState((prev) => ({
        ...prev,
        templateMetadata: prev.templateMetadata
          ? { ...prev.templateMetadata, ...updates }
          : {
              name: updates.name || 'Untitled Template',
              status: updates.status || 'DRAFT',
              version: updates.version || '1.0.0',
              key: updates.key || undefined,
              keyName: updates.keyName || undefined,
              ...updates,
            },
        isDirty: true,
      }));
    },
    []
  );

  // Set template metadata (for initial load)
  const setTemplateMetadata = useCallback((metadata: TemplateMetadata) => {
    setState((prev) => ({
      ...prev,
      templateMetadata: metadata,
    }));
  }, []);

  // Save template to API
  const saveTemplateToAPI = useCallback(
    async (
      metadata?: Partial<TemplateMetadata>
    ): Promise<{
      success: boolean;
      templateId?: string;
      template?: IFormsTemplate;
    }> => {
      setIsSaving(true);

      const currentMetadata = state.templateMetadata;
      const mergedMetadata: TemplateMetadata = {
        name: metadata?.name || currentMetadata?.name || 'Untitled Template',
        description: metadata?.description ?? currentMetadata?.description,
        status: metadata?.status || currentMetadata?.status || 'DRAFT',
        version: metadata?.version || currentMetadata?.version || '1.0.0',
        versionNote: metadata?.versionNote ?? currentMetadata?.versionNote,
        key: metadata?.key ?? currentMetadata?.key,
        id: metadata?.id ?? currentMetadata?.id,
        keyName: metadata?.keyName ?? currentMetadata?.keyName,
      };

      // Update DSL with exportedAt timestamp before saving
      const dslToSave: FormDSL = {
        ...state.dsl,
        exportedAt: new Date().toISOString(),
      };

      try {
        // Update existing template
        if (mergedMetadata.id) {
          const result = await updateFormsTemplate(mergedMetadata.id, {
            name: mergedMetadata.name,
            description: mergedMetadata.description,
            status: mergedMetadata.status as FormsTemplateStatus,
            dsl: dslToSave as unknown as Record<string, unknown>,
            version: mergedMetadata.version,
            versionNote: mergedMetadata.versionNote,
            key: mergedMetadata.key || undefined,
          });

          if (result.success) {
            const newTemplate = result.data?.formsTemplate;
            const newTemplateId = newTemplate?.id || mergedMetadata.id;
            setState((prev) => ({
              ...prev,
              dsl: dslToSave,
              templateMetadata: {
                ...mergedMetadata,
                id: newTemplateId,
                keyName: mergedMetadata.keyName || undefined,
              },
              isDirty: false,
              lastSaved: new Date(),
            }));
            return {
              success: true,
              templateId: newTemplateId,
              template: newTemplate,
            };
          }

          return { success: false };
        }

        // Create new template
        const result = await createFormsTemplate({
          name: mergedMetadata.name,
          description: mergedMetadata.description,
          type: FormsTemplateType.FORM_TEMPLATE,
          status: mergedMetadata.status as FormsTemplateStatus,
          dsl: dslToSave as unknown as Record<string, unknown>,
          version: mergedMetadata.version,
          versionNote: mergedMetadata.versionNote,
          key: mergedMetadata.key,
        });

        if (result.success && result.data) {
          const { id: newTemplateId } = result.data.formsTemplate;
          setState((prev) => ({
            ...prev,
            dsl: dslToSave,
            templateMetadata: { ...mergedMetadata, id: newTemplateId },
            isDirty: false,
            lastSaved: new Date(),
          }));

          try {
            mixpanel.track(MIXPANEL_EVENTS.FORM_CREATED, {
              surface_area: MIXPANEL_SURFACE_AREA.WORKFLOW_AND_FORM_BUILDER,
              form_id: newTemplateId,
              form_name: mergedMetadata.name,
            });
          } catch (error) {
            console.error('Failed to track form created event:', error);
          }
          return { success: true, templateId: newTemplateId };
        }

        return { success: false };
      } catch (error) {
        console.error('Failed to save template:', error);
        return { success: false };
      } finally {
        setIsSaving(false);
      }
    },
    [state.dsl, state.templateMetadata]
  );

  // Load template from API response
  const loadFromTemplate = useCallback(
    (template: IFormsTemplate, isDuplicate = false) => {
      setState({
        dsl: template.dsl as unknown as FormDSL,
        selection: { type: null },
        isDirty: isDuplicate,
        lastSaved: isDuplicate ? undefined : new Date(template.updatedAt),
        templateMetadata: isDuplicate
          ? {
              name: `${template.name} (Copy)`,
              description: template.description || undefined,
              status: 'DRAFT',
              version: '1.0.0',
              versionNote: `Duplicated from ${template.name} v${template.version}`,
              key: template.key || undefined,
              keyName: template.keyName || undefined,
            }
          : {
              id: template.id,
              name: template.name,
              description: template.description || undefined,
              status: template.status as 'DRAFT' | 'LIVE' | 'COMPLETED',
              version: template.version,
              versionNote: template.versionNote || undefined,
              key: template.key || undefined,
              keyName: template.keyName || undefined,
            },
      });
    },
    []
  );

  // Update DSL helper
  const updateDSL = useCallback((updater: (dsl: FormDSL) => FormDSL) => {
    setState((prev) => ({
      ...prev,
      dsl: updater(prev.dsl),
      isDirty: true,
    }));
  }, []);

  // Selection
  const setSelection = useCallback((selection: Selection) => {
    setState((prev) => ({ ...prev, selection }));
  }, []);

  const clearSelection = useCallback(() => {
    setState((prev) => ({ ...prev, selection: { type: null } }));
  }, []);

  // Form metadata
  const updateFormMetadata = useCallback(
    (updates: Partial<FormDSL['form']>) => {
      updateDSL((dsl) => ({
        ...dsl,
        form: { ...dsl.form, ...updates },
      }));
    },
    [updateDSL]
  );

  // Step operations
  const addStep = useCallback(
    (title = 'New Step') => {
      const newStep: FormStep = {
        id: generateId('step'),
        title,
        order: state?.dsl?.steps?.length ? state?.dsl?.steps?.length + 1 : 1,
        estimatedMinutes: 5,
        modules: [
          {
            id: generateId('module'),
            name: 'Section 1',
            order: 1,
            components: [],
          },
        ],
      };
      updateDSL((dsl) => ({
        ...dsl,
        steps: [...(dsl?.steps ?? []), newStep],
      }));
      return newStep.id;
    },
    [state?.dsl?.steps?.length, updateDSL]
  );

  // Add step from a saved template DSL
  const addStepFromTemplate = useCallback(
    (stepDsl: FormStep) => {
      // Generate new IDs to avoid conflicts
      const newStepId = generateId('step');
      const newModules = stepDsl.modules.map((module) => ({
        ...module,
        id: generateId('module'),
        components: module.components.map((comp) => ({
          ...comp,
          id: generateId('component'),
          fieldId: comp.fieldId ? generateId(comp.component) : undefined,
        })),
      }));

      const newStep: FormStep = {
        ...stepDsl,
        id: newStepId,
        order: state?.dsl?.steps?.length ? state?.dsl?.steps?.length + 1 : 1,
        modules: newModules,
      };

      updateDSL((dsl) => ({
        ...dsl,
        steps: [...(dsl?.steps ?? []), newStep],
      }));
      return newStep.id;
    },
    [state?.dsl?.steps?.length, updateDSL]
  );

  const updateStep = useCallback(
    (stepId: string, updates: Partial<FormStep>) => {
      updateDSL((dsl) => ({
        ...dsl,
        steps: dsl.steps.map((step) =>
          step.id === stepId ? { ...step, ...updates } : step
        ),
      }));
    },
    [updateDSL]
  );

  const removeStep = useCallback(
    (stepId: string) => {
      updateDSL((dsl) => ({
        ...dsl,
        steps: dsl.steps
          .filter((step) => step.id !== stepId)
          .map((step, index) => ({ ...step, order: index + 1 })),
      }));
      clearSelection();
    },
    [updateDSL, clearSelection]
  );

  const reorderSteps = useCallback(
    (fromIndex: number, toIndex: number) => {
      updateDSL((dsl) => {
        const newSteps = [...dsl?.steps];
        const [removed] = newSteps.splice(fromIndex, 1);
        newSteps.splice(toIndex, 0, removed);
        return {
          ...dsl,
          steps: newSteps.map((step, index) => ({ ...step, order: index + 1 })),
        };
      });
    },
    [updateDSL]
  );

  // Module operations
  const addModule = useCallback(
    (stepId: string, name = 'New Section') => {
      const step = state?.dsl?.steps?.find((s) => s.id === stepId);
      if (!step) return null;

      const newModule: FormModule = {
        id: generateId('module'),
        name,
        order: step.modules.length + 1,
        components: [],
      };

      updateDSL((dsl) => ({
        ...dsl,
        steps: dsl.steps.map((s) =>
          s.id === stepId ? { ...s, modules: [...s.modules, newModule] } : s
        ),
      }));
      return newModule.id;
    },
    [state?.dsl?.steps, updateDSL]
  );

  // Add a module from a saved template (with all its components)
  const addModuleFromTemplate = useCallback(
    (
      stepId: string,
      templateModule: {
        name: string;
        description?: string;
        components: FormComponent[];
      }
    ) => {
      const step = state?.dsl?.steps?.find((s) => s.id === stepId);
      if (!step) return null;

      // Generate new IDs for all components to avoid conflicts
      const newComponents = templateModule.components.map((comp, index) => ({
        ...comp,
        id: generateId('component'),
        fieldId: comp.fieldId ? generateId(comp.component) : undefined,
        order: index + 1,
      }));

      const newModule: FormModule = {
        id: generateId('module'),
        name: templateModule.name,
        description: templateModule.description,
        order: step.modules.length + 1,
        components: newComponents,
      };

      updateDSL((dsl) => ({
        ...dsl,
        steps: dsl.steps.map((s) =>
          s.id === stepId ? { ...s, modules: [...s.modules, newModule] } : s
        ),
      }));
      return newModule.id;
    },
    [state?.dsl?.steps, updateDSL]
  );

  const updateModule = useCallback(
    (stepId: string, moduleId: string, updates: Partial<FormModule>) => {
      updateDSL((dsl) => ({
        ...dsl,
        steps: dsl.steps.map((step) =>
          step.id === stepId
            ? {
                ...step,
                modules: step.modules.map((mod) =>
                  mod.id === moduleId ? { ...mod, ...updates } : mod
                ),
              }
            : step
        ),
      }));
    },
    [updateDSL]
  );

  const removeModule = useCallback(
    (stepId: string, moduleId: string) => {
      updateDSL((dsl) => ({
        ...dsl,
        steps: dsl.steps.map((step) =>
          step.id === stepId
            ? {
                ...step,
                modules: step.modules
                  .filter((mod) => mod.id !== moduleId)
                  .map((mod, index) => ({ ...mod, order: index + 1 })),
              }
            : step
        ),
      }));
      clearSelection();
    },
    [updateDSL, clearSelection]
  );

  const reorderModules = useCallback(
    (stepId: string, fromIndex: number, toIndex: number) => {
      updateDSL((dsl) => ({
        ...dsl,
        steps: dsl.steps.map((step) => {
          if (step.id !== stepId) return step;
          const newModules = [...step.modules];
          const [removed] = newModules.splice(fromIndex, 1);
          newModules.splice(toIndex, 0, removed);
          return {
            ...step,
            modules: newModules.map((mod, index) => ({
              ...mod,
              order: index + 1,
            })),
          };
        }),
      }));
    },
    [updateDSL]
  );

  // Creates a new module with a single component atomically (avoids stale-state issues)
  const addModuleWithSingleComponent = useCallback(
    (stepId: string, componentConfig: Partial<FormComponent>) => {
      const step = state?.dsl?.steps?.find((s) => s.id === stepId);
      if (!step) return null;

      const newComponentId = generateId('component');
      const newComponent: FormComponent = {
        id: newComponentId,
        component:
          (componentConfig.component as FormComponent['component']) || 'Input',
        order: 1,
        ...componentConfig,
        fieldId: generateId(componentConfig.component || 'field'),
      };

      const newModuleId = generateId('module');
      const newModule: FormModule = {
        id: newModuleId,
        name: (componentConfig.label as string) || 'New Section',
        description: componentConfig.description as string | undefined,
        order: step.modules.length + 1,
        components: [newComponent],
      };

      updateDSL((dsl) => ({
        ...dsl,
        steps: dsl.steps.map((s) =>
          s.id === stepId ? { ...s, modules: [...s.modules, newModule] } : s
        ),
      }));

      return { moduleId: newModuleId, componentId: newComponentId };
    },
    [state?.dsl?.steps, updateDSL]
  );

  // Component operations
  const addComponent = useCallback(
    (
      stepId: string,
      moduleId: string,
      componentConfig: Partial<FormComponent>,
      atIndex?: number
    ) => {
      const step = state?.dsl?.steps?.find((s) => s.id === stepId);
      const targetModule = step?.modules.find((m) => m.id === moduleId);
      if (!targetModule) return null;

      // Module-locking components (Ownership / KeyPersons / NestedForm) each own
      // their whole section. Into a non-empty section → give it its own section;
      // into an empty section → fill that section and adopt the component's
      // label/description as the section title/description. Nothing else may be
      // added to a section that already holds a locking component.
      const isLockingComponent = isModuleLockingComponent(
        componentConfig.component
      );
      const moduleHasLockingComponent = targetModule.components.some((c) =>
        isModuleLockingComponent(c.component)
      );
      if (isLockingComponent && targetModule.components.length > 0) {
        return (
          addModuleWithSingleComponent(stepId, componentConfig)?.componentId ??
          null
        );
      }
      if (moduleHasLockingComponent) {
        return null;
      }
      // A locking component into an empty section makes that section its own:
      // adopt its label/description so the section renders the node's title + desc.
      const fillSectionIdentity = isLockingComponent;

      const newComponent: FormComponent = {
        id: generateId('component'),
        component: componentConfig.component || 'Input',
        order:
          atIndex !== undefined
            ? atIndex + 1
            : targetModule.components.length + 1,
        ...componentConfig,
        fieldId:
          componentConfig.fieldId ||
          generateId(componentConfig.component || 'field'),
      };

      updateDSL((dsl) => ({
        ...dsl,
        steps: dsl.steps.map((s) =>
          s.id === stepId
            ? {
                ...s,
                modules: s.modules.map((m) => {
                  if (m.id !== moduleId) return m;
                  const newComponents = [...m.components];
                  if (atIndex !== undefined) {
                    newComponents.splice(atIndex, 0, newComponent);
                  } else {
                    newComponents.push(newComponent);
                  }
                  return {
                    ...m,
                    ...(fillSectionIdentity
                      ? {
                          name: (componentConfig.label as string) ?? m.name,
                          description:
                            (componentConfig.description as
                              | string
                              | undefined) ?? m.description,
                        }
                      : {}),
                    components: newComponents.map((c, i) => ({
                      ...c,
                      order: i + 1,
                    })),
                  };
                }),
              }
            : s
        ),
      }));
      return newComponent.id;
    },
    [state?.dsl?.steps, updateDSL, addModuleWithSingleComponent]
  );

  const updateComponent = useCallback(
    (
      stepId: string,
      moduleId: string,
      componentId: string,
      updates: Partial<FormComponent>
    ) => {
      updateDSL((dsl) => ({
        ...dsl,
        steps: dsl.steps.map((step) =>
          step.id === stepId
            ? {
                ...step,
                modules: step.modules.map((mod) =>
                  mod.id === moduleId
                    ? {
                        ...mod,
                        components: mod.components.map((comp) =>
                          comp.id === componentId
                            ? { ...comp, ...updates }
                            : comp
                        ),
                      }
                    : mod
                ),
              }
            : step
        ),
      }));
    },
    [updateDSL]
  );

  const removeComponent = useCallback(
    (stepId: string, moduleId: string, componentId: string) => {
      updateDSL((dsl) => ({
        ...dsl,
        steps: dsl.steps.map((step) =>
          step.id === stepId
            ? {
                ...step,
                modules: step.modules.map((mod) =>
                  mod.id === moduleId
                    ? {
                        ...mod,
                        components: mod.components
                          .filter((c) => c.id !== componentId)
                          .map((c, i) => ({ ...c, order: i + 1 })),
                      }
                    : mod
                ),
              }
            : step
        ),
      }));
      clearSelection();
    },
    [updateDSL, clearSelection]
  );

  const duplicateComponent = useCallback(
    (stepId: string, moduleId: string, componentId: string) => {
      const step = state?.dsl?.steps?.find((s) => s.id === stepId);
      const targetModule = step?.modules.find((m) => m.id === moduleId);
      if (!targetModule) return null;

      const componentToDuplicate = targetModule.components.find(
        (c) => c.id === componentId
      );
      if (!componentToDuplicate) return null;

      // Sort components by order to find the correct index
      const sortedComponents = [...targetModule.components].sort(
        (a, b) => a.order - b.order
      );
      const currentIndex = sortedComponents.findIndex(
        (c) => c.id === componentId
      );

      // Create duplicate with new IDs
      const duplicatedComponent: FormComponent = {
        ...componentToDuplicate,
        id: generateId('component'),
        fieldId: generateId(componentToDuplicate.component || 'field'),
        order: currentIndex + 2, // Will be recalculated after insertion
      };

      // Insert after the current component
      const atIndex = currentIndex + 1;

      updateDSL((dsl) => ({
        ...dsl,
        steps: dsl.steps.map((s) =>
          s.id === stepId
            ? {
                ...s,
                modules: s.modules.map((m) => {
                  if (m.id !== moduleId) return m;
                  const newComponents = [...m.components];
                  newComponents.splice(atIndex, 0, duplicatedComponent);
                  return {
                    ...m,
                    components: newComponents.map((c, i) => ({
                      ...c,
                      order: i + 1,
                    })),
                  };
                }),
              }
            : s
        ),
      }));

      return duplicatedComponent.id;
    },
    [state?.dsl?.steps, updateDSL]
  );

  const reorderComponents = useCallback(
    (stepId: string, moduleId: string, fromIndex: number, toIndex: number) => {
      updateDSL((dsl) => ({
        ...dsl,
        steps: dsl.steps.map((step) => {
          if (step.id !== stepId) return step;
          return {
            ...step,
            modules: step.modules.map((mod) => {
              if (mod.id !== moduleId) return mod;
              // Sort components by order first to match the visual order
              const sortedComponents = [...mod.components].sort(
                (a, b) => a.order - b.order
              );
              const [removed] = sortedComponents.splice(fromIndex, 1);
              sortedComponents.splice(toIndex, 0, removed);
              return {
                ...mod,
                components: sortedComponents.map((c, i) => ({
                  ...c,
                  order: i + 1,
                })),
              };
            }),
          };
        }),
      }));
    },
    [updateDSL]
  );

  const moveComponent = useCallback(
    (
      fromStepId: string,
      fromModuleId: string,
      componentId: string,
      toStepId: string,
      toModuleId: string,
      toIndex: number
    ) => {
      updateDSL((dsl) => {
        let movedComponent: FormComponent | null = null;

        // Remove from source
        const stepsAfterRemove = dsl.steps.map((step) => {
          if (step.id !== fromStepId) return step;
          return {
            ...step,
            modules: step.modules.map((mod) => {
              if (mod.id !== fromModuleId) return mod;
              const componentToMove = mod.components.find(
                (c) => c.id === componentId
              );
              if (componentToMove) {
                movedComponent = componentToMove;
              }
              return {
                ...mod,
                components: mod.components
                  .filter((c) => c.id !== componentId)
                  .map((c, i) => ({ ...c, order: i + 1 })),
              };
            }),
          };
        });

        if (!movedComponent) return dsl;

        // Add to destination
        return {
          ...dsl,
          steps: stepsAfterRemove.map((step) => {
            if (step.id !== toStepId) return step;
            return {
              ...step,
              modules: step.modules.map((mod) => {
                if (mod.id !== toModuleId) return mod;
                const newComponents = [...mod.components];
                newComponents.splice(toIndex, 0, movedComponent!);
                return {
                  ...mod,
                  components: newComponents.map((c, i) => ({
                    ...c,
                    order: i + 1,
                  })),
                };
              }),
            };
          }),
        };
      });
    },
    [updateDSL]
  );

  // Import/Export
  const exportToJSON = useCallback(() => {
    const exportTimestamp = new Date().toISOString();
    const exportData = {
      dsl: {
        ...state.dsl,
        exportedAt: exportTimestamp,
      },
      exportedAt: exportTimestamp,
    };
    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `FB_${state.dsl.form.title}_${Date.now().toString()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [state.dsl]);

  const importFromJSON = useCallback((file: File): Promise<void> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const content = e.target?.result as string;
          const imported = JSON.parse(content);

          // Handle both old format (just DSL) and new format (with wrapper)
          const dsl = imported.dsl || imported;

          setState((prev) => ({
            ...prev,
            dsl,
            isDirty: true,
            selection: { type: null },
          }));
          resolve();
        } catch (error) {
          reject(new Error('Invalid JSON file'));
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  }, []);

  // Reset to empty
  const resetForm = useCallback(() => {
    setState({
      dsl: createEmptyDSL(),
      selection: { type: null },
      isDirty: true,
      templateMetadata: undefined,
    });
  }, []);

  // Load specific DSL
  const loadDSL = useCallback((dsl: FormDSL) => {
    setState({
      dsl,
      selection: { type: null },
      isDirty: false,
    });
  }, []);

  // Get selected item details
  const getSelectedItem = useCallback(() => {
    const { selection } = state;
    if (!selection.type) return null;

    if (selection.type === 'step' && selection.stepId) {
      return state?.dsl?.steps?.find((s) => s.id === selection.stepId);
    }

    if (selection.type === 'module' && selection.stepId && selection.moduleId) {
      const step = state?.dsl?.steps?.find((s) => s.id === selection.stepId);
      return step?.modules.find((m) => m.id === selection.moduleId);
    }

    if (
      selection.type === 'component' &&
      selection.stepId &&
      selection.moduleId &&
      selection.componentId
    ) {
      const step = state?.dsl?.steps?.find((s) => s.id === selection.stepId);
      const selectedModule = step?.modules.find(
        (m) => m.id === selection.moduleId
      );
      return selectedModule?.components.find(
        (c) => c.id === selection.componentId
      );
    }

    return null;
  }, [state]);

  return {
    // State
    dsl: state.dsl,
    selection: state.selection,
    isDirty: state.isDirty,
    lastSaved: state.lastSaved,
    templateMetadata: state.templateMetadata,
    isSaving,

    // Selection
    setSelection,
    clearSelection,
    getSelectedItem,

    // Form metadata
    updateFormMetadata,

    // Template metadata
    updateTemplateMetadata,
    setTemplateMetadata,

    // Step operations
    addStep,
    addStepFromTemplate,
    updateStep,
    removeStep,
    reorderSteps,

    // Module operations
    addModule,
    addModuleFromTemplate,
    addModuleWithSingleComponent,
    updateModule,
    removeModule,
    reorderModules,

    // Component operations
    addComponent,
    updateComponent,
    removeComponent,
    duplicateComponent,
    reorderComponents,
    moveComponent,

    // Persistence
    saveNow,
    saveTemplateToAPI,
    loadFromTemplate,
    exportToJSON,
    importFromJSON,
    resetForm,
    loadDSL,
  };
}

export type FormBuilderHook = ReturnType<typeof useFormBuilder>;
